import { EnterSearchInput } from "./EnterSearchInput";
import { ClientTable } from "./ClientTable";
import { Search } from "lucide-react";
import { PageTitleRow } from "../layout/PageTitleRow";
import { PageNavigationContext } from "../layout/PageNavigation";
import { cn } from "../../lib/utils";

/** Design tokens shared with WelcomePage / Leave Management */
export const ui = {
  card: "bg-card rounded-2xl shadow-[0_8px_28px_-16px_rgba(36,51,72,0.18)] border border-border",
  cardHeader:
    "px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex items-center justify-between gap-2 flex-wrap",
  pageTitle: "text-xl sm:text-[22px] leading-snug font-semibold tracking-tight text-foreground",
  pageSub: "text-sm per-file-hint mt-1",
  sectionLabel:
    "text-[11px] font-bold per-file-hint uppercase tracking-wider",
  input:
    "h-9 sm:h-10 rounded-xl border border-(--color-text-border) px-3 text-sm bg-card focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10",
  tableHead:
    "text-left text-[10px] uppercase tracking-wider per-file-hint bg-muted",
  primaryBtn:
    "inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-accent transition shadow-[0_8px_20px_-8px_rgba(79,70,229,0.3)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
  filterBtn:
    "inline-flex items-center justify-center gap-1.5 h-9 px-2.5 sm:px-3 rounded-xl border border-(--color-text-border) bg-card text-[12px] sm:text-sm font-semibold per-file-hint hover:text-primary hover:border-primary/40 transition cursor-pointer",
};

export function PageHeader({ title, action, back, className, inlineControls = false }) {
  if (inlineControls) {
    return (
      <PageNavigationContext.Provider value={undefined}>
        <div className={cn("page-header page-header--inline mb-6", className)}>
          <PageTitleRow back={back}>
            <h1 className={ui.pageTitle}>{title}</h1>
            {action ? <div className="page-header__inline-action">{action}</div> : null}
          </PageTitleRow>
        </div>
      </PageNavigationContext.Provider>
    );
  }
  return (
    <div
      className={cn(
        "page-header mb-6",
        className
      )}
    >
      <PageTitleRow back={back}>
        <h1 className={ui.pageTitle}>{title}</h1>
      </PageTitleRow>
      {action ? <div className="page-header__actions">{action}</div> : null}
    </div>
  );
}

export function ContentCard({ title, action, children, className, bodyClassName }) {
  return (
    <div className={cn(ui.card, "content-card overflow-hidden", className)}>
      {title ? (
        <div className={cn(ui.cardHeader, "content-card__header")}>
          <h2 className="text-[13px] sm:text-[15px] font-semibold text-foreground">{title}</h2>
          {action}
        </div>
      ) : null}
      <div className={cn("content-card__body", bodyClassName)}>{children}</div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search...", className, submitOnEnter = false, ...props }) {
  const Input = submitOnEnter ? EnterSearchInput : "input";
  return (
    <div className="page-search relative w-full sm:w-[280px]">
      <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint" />
      <Input {...props} data-page-search="true" type="search" value={value}
        onChange={onChange} placeholder={placeholder} aria-label={props["aria-label"] || placeholder}
        className={cn(ui.input, "h-9 w-full pl-9", className)} />
    </div>
  );
}

export function LoadingState({ message = "Loading...", className }) {
  return (
    <div
      className={cn(
        "p-8 sm:p-12 text-center text-sm per-file-hint",
        className
      )}
    >
      {message}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, message, action, children, className }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center p-8 sm:p-12 text-center",
        className
      )}
    >
      {Icon ? (
        <div className="mb-3 h-12 w-12 shrink-0 rounded-2xl bg-workspace/60 grid place-items-center per-file-hint">
          <Icon className="h-6 w-6" />
        </div>
      ) : null}
      {title ? <p className="text-sm font-semibold text-foreground">{title}</p> : null}
      {message ? (
        <p className="text-sm per-file-hint mt-1 max-w-md">{message}</p>
      ) : null}
      {action || children ? <div className="mt-4">{action || children}</div> : null}
    </div>
  );
}

export function AlertBanner({ type = "success", children, className }) {
  const styles = {
    success:
      "bg-success/10 border-success/25 text-success",
    error: "bg-error/10 border-error/25 text-error",
    info: "bg-accent/5 border-accent/20 text-foreground",
  };
  return (
    <div
      className={cn(
        "mb-4 p-3 sm:p-4 rounded-xl border text-sm font-semibold",
        styles[type] || styles.info,
        className
      )}
    >
      {children}
    </div>
  );
}

export function DataTable({ columns, children, minWidth = "760px", className, paginate = false }) {
  const Table = paginate ? ClientTable : "table";
  return (
    <div className={cn("hidden md:block overflow-x-auto", className)}>
      <Table className="w-full text-sm" style={{ minWidth }}>
        <thead>
          <tr className={ui.tableHead}>
            {columns.map((col) => (
              <th key={col.key || col} className={cn("px-4 py-3 font-semibold", col.className)}>
                {col.label || col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-(--color-text-border)/35">{children}</tbody>
      </Table>
    </div>
  );
}
